import { useQuery } from '@tanstack/react-query'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useDebouncedValue } from '@omnea-app/frontend/utils/hooks/useDebouncedValue'
import { throwIfApiError } from '@omnea-app/legacy/utils/error-handling/throwifApiError'
import { ProductsHttpClient } from '@omnea-app/products/productsHttpClient'
import { paths } from '@omnea-app/products/productsHttpClient/productsGeneratedTypes'

export const SearchSuppliersQueryKey = (name: string, includeGlobal: boolean) => ['searchSuppliersQueryKey', name, includeGlobal]

type SearchQuery = NonNullable<paths['/search']['get']['parameters']['query']>

const useQueryFn = ({ name, includeGlobal }: { name: string; includeGlobal: boolean }) => {
  const { getToken } = useAuthenticatedUser()

  return async () => {
    const token = await getToken()
    const apiClient = new ProductsHttpClient({ authorization: token })

    const query: SearchQuery = {
      name,
      includeGlobal: includeGlobal ? 'true' : 'false',
    }

    const { data, error } = await apiClient.GET('/search', {
      params: {
        query,
      },
    })
    throwIfApiError(error)

    // THis currently returns both suppliers and products - in this hook we only want suppliers
    const suppliers = data.suppliers ?? []

    return {
      suppliers,
    }
  }
}

export const useSearchSuppliers = ({ name, includeGlobal }: { name: string; includeGlobal: boolean }) => {
  const debouncedName = useDebouncedValue(name, { delay: 150 })

  return useQuery({
    queryKey: SearchSuppliersQueryKey(debouncedName, includeGlobal),
    queryFn: useQueryFn({ name: debouncedName, includeGlobal }),
    enabled: debouncedName.length > 2,
  })
}
